// src/components/payments/PaymentSuccess.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { paymentsService } from '../../services/paymentsService';
import { usePageTitle } from '../../hooks/usePageTitle';
import './PaymentResult.css';

const PaymentSuccess = () => {
    usePageTitle('Payment Successful');
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [verifying, setVerifying] = useState(true);
    const [error, setError] = useState(null);
    const [paymentDetails, setPaymentDetails] = useState(null);

    useEffect(() => {
        // Check if we already verified this payment in this session
        const currentUrl = window.location.href;
        const sessionKey = `payment_verified_${currentUrl}`;
        const cachedResult = sessionStorage.getItem(sessionKey);

        if (cachedResult) {
            console.log('Using cached payment verification result');
            const cached = JSON.parse(cachedResult);
            setPaymentDetails(cached);
            setVerifying(false);
            setLoading(false);
            return;
        }

        verifyPayment();
    }, []);

    const verifyPayment = async () => {
        try {
            setVerifying(true);

            // Handle malformed URLs with double question marks (Stripe bug)
            // Example: ?gateway=stripe?session_id=cs_test_xxx
            const urlString = window.location.href;
            let cleanedSearchParams = searchParams;

            // Check if URL has double question marks
            if (urlString.includes('??') || (urlString.match(/\?/g) || []).length > 1) {
                console.warn('Detected malformed URL with multiple question marks, attempting to fix...');

                // Extract the query string part
                const queryStart = urlString.indexOf('?');
                const queryString = urlString.substring(queryStart + 1);

                // Replace additional ? with & to fix the URL
                const fixedQueryString = queryString.replace(/\?/g, '&');

                // Create new URLSearchParams from fixed string
                cleanedSearchParams = new URLSearchParams(fixedQueryString);

                console.log('Original URL:', urlString);
                console.log('Fixed query string:', fixedQueryString);
            }

            // Log all URL parameters for debugging
            const allParams = {};
            for (let [key, value] of cleanedSearchParams.entries()) {
                allParams[key] = value;
            }
            console.log('=== PaymentSuccess URL Parameters ===');
            console.log('Full URL:', window.location.href);
            console.log('All parameters:', allParams);

            // Get payment reference from URL params
            // Different gateways use different parameter names
            const sessionId = cleanedSearchParams.get('session_id'); // Stripe Checkout Session
            const reference = cleanedSearchParams.get('reference'); // Paystack/Flutterwave
            const trxref = cleanedSearchParams.get('trxref'); // Alternative reference param
            const paymentIntent = cleanedSearchParams.get('payment_intent'); // Stripe Payment Intent
            const transactionId = cleanedSearchParams.get('transaction_id'); // Razorpay
            const txRef = cleanedSearchParams.get('tx_ref'); // Flutterwave alternative

            // Log all possible reference parameters
            console.log('Reference parameters found:', {
                session_id: sessionId,
                reference: reference,
                trxref: trxref,
                payment_intent: paymentIntent,
                transaction_id: transactionId,
                tx_ref: txRef
            });

            // Determine the actual reference to use (priority order)
            const paymentReference = sessionId || reference || trxref || paymentIntent || transactionId || txRef;

            if (!paymentReference) {
                console.error('No payment reference found in any parameter');
                setError('No payment reference found in URL. If you just made a payment, please wait a moment for the redirect.');
                setVerifying(false);
                setLoading(false);
                return;
            }

            console.log('Selected payment reference:', paymentReference);

            // Get gateway from URL params or try to auto-detect
            let detectedGateway = cleanedSearchParams.get('gateway') || cleanedSearchParams.get('payment_gateway');
            console.log('Gateway from URL parameter:', detectedGateway);

            // If no explicit gateway param, try to detect from reference format
            // Note: Our backend generates all refs with PAY_ prefix, so we prefer the gateway param
            if (!detectedGateway) {
                console.log('No gateway parameter, attempting auto-detection...');

                // Stripe Checkout Session IDs
                if (sessionId && paymentReference.startsWith('cs_')) {
                    detectedGateway = 'stripe';
                    console.log('Detected Stripe from cs_ prefix');
                }
                // Stripe Payment Intent IDs
                else if (paymentIntent && paymentReference.startsWith('pi_')) {
                    detectedGateway = 'stripe';
                    console.log('Detected Stripe from pi_ prefix');
                }
                // Flutterwave transaction references
                else if (paymentReference.startsWith('flw_') || txRef) {
                    detectedGateway = 'flutterwave';
                    console.log('Detected Flutterwave from flw_ prefix or tx_ref param');
                }
                // Razorpay payment/order IDs
                else if (paymentReference.startsWith('pay_') || paymentReference.startsWith('order_')) {
                    detectedGateway = 'razorpay';
                    console.log('Detected Razorpay from pay_/order_ prefix');
                }
                // JazzCash/EasyPaisa references (PP format)
                else if (paymentReference.startsWith('PP')) {
                    detectedGateway = 'jazzcash'; // or easypaisa
                    console.log('Detected JazzCash/EasyPaisa from PP prefix');
                }
                else {
                    // Cannot reliably detect gateway - show error
                    console.error('Cannot auto-detect gateway from reference:', paymentReference);
                    setError('Payment gateway not specified. Unable to verify payment. Please check your subscription page or contact support.');
                    setVerifying(false);
                    setLoading(false);
                    return;
                }
            }

            console.log('=== Final Payment Verification Details ===');
            console.log('Gateway:', detectedGateway);
            console.log('Reference:', paymentReference);

            // Verify payment with backend
            const result = await paymentsService.verifyPayment({
                gateway_code: detectedGateway,
                reference: paymentReference
            });

            console.log('Payment verification successful:', result);

            // Cache the result to prevent re-verification on reload
            const currentUrl = window.location.href;
            const sessionKey = `payment_verified_${currentUrl}`;
            sessionStorage.setItem(sessionKey, JSON.stringify(result));

            setPaymentDetails(result);
            setVerifying(false);
        } catch (err) {
            console.error('Payment verification error:', err);

            // Check if it's a "not found" error
            if (err.message?.includes('not found') || err.message?.includes('Transaction reference')) {
                setError('This payment reference could not be found. It may not have been processed yet, or the payment was cancelled. Please check your subscription page or contact support if you believe this is an error.');
            } else {
                setError(err.message || 'Failed to verify payment');
            }

            setVerifying(false);
        } finally {
            setLoading(false);
        }
    };

    const handleGoToDashboard = () => {
        navigate('/dashboard');
    };

    const handleGoToSubscriptions = () => {
        navigate('/subscriptions');
    };

    if (loading || verifying) {
        return (
            <div className="payment-result-container">
                <div className="payment-result-card">
                    <div className="payment-result-loading">
                        <div className="spinner-large"></div>
                        <h2>Verifying Payment...</h2>
                        <p>Please wait while we confirm your payment</p>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="payment-result-container">
                <div className="payment-result-card error">
                    <div className="payment-result-icon">
                        <i className="fas fa-exclamation-triangle"></i>
                    </div>
                    <h1>Verification Error</h1>
                    <p className="payment-result-message">{error}</p>

                    <div className="payment-result-info">
                        <h4>What should I do?</h4>
                        <ul>
                            <li>Check your subscription page to see if the payment was processed</li>
                            <li>If you see a pending subscription, you can retry the payment</li>
                            <li>Contact support if the issue persists or if you were charged but don't see a subscription</li>
                        </ul>
                    </div>

                    <div className="payment-result-actions">
                        <button className="btn btn-primary" onClick={handleGoToSubscriptions}>
                            <i className="fas fa-credit-card"></i>
                            View Subscriptions
                        </button>
                        <button className="btn btn-secondary" onClick={handleGoToDashboard}>
                            <i className="fas fa-home"></i>
                            Go to Dashboard
                        </button>
                    </div>

                    <div className="payment-help">
                        <p>
                            <i className="fas fa-question-circle"></i>
                            Need help? Contact our support team at{' '}
                            <a href="mailto:support@ehaazri.com">support@ehaazri.com</a>
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="payment-result-container">
            <div className="payment-result-card success">
                <div className="payment-result-icon">
                    <i className="fas fa-check-circle"></i>
                </div>
                <h1>Payment Successful!</h1>
                <p className="payment-result-message">
                    Your payment has been processed successfully. Your subscription is now active!
                </p>

                {paymentDetails && (
                    <div className="payment-details">
                        <h3>Payment Details</h3>
                        <div className="payment-details-grid">
                            {paymentDetails.reference && (
                                <div className="payment-detail-item">
                                    <span className="label">Reference:</span>
                                    <span className="value">{paymentDetails.reference}</span>
                                </div>
                            )}
                            {paymentDetails.amount && (
                                <div className="payment-detail-item">
                                    <span className="label">Amount:</span>
                                    <span className="value">
                                        {paymentDetails.currency} {parseFloat(paymentDetails.amount).toFixed(2)}
                                    </span>
                                </div>
                            )}
                            {paymentDetails.staff_count && (
                                <div className="payment-detail-item">
                                    <span className="label">License Count:</span>
                                    <span className="value">{paymentDetails.staff_count} staff members</span>
                                </div>
                            )}
                            {paymentDetails.license_end_date && (
                                <div className="payment-detail-item">
                                    <span className="label">Valid Until:</span>
                                    <span className="value">
                                        {new Date(paymentDetails.license_end_date).toLocaleDateString()}
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                <div className="payment-result-actions">
                    <button className="btn btn-primary" onClick={handleGoToSubscriptions}>
                        <i className="fas fa-credit-card"></i>
                        View Subscription
                    </button>
                    <button className="btn btn-secondary" onClick={handleGoToDashboard}>
                        <i className="fas fa-home"></i>
                        Go to Dashboard
                    </button>
                </div>

                <div className="payment-next-steps">
                    <h4>What's Next?</h4>
                    <ul>
                        <li>
                            <i className="fas fa-users"></i>
                            Upload your staff members (up to {paymentDetails?.staff_count || 'your license'} count)
                        </li>
                        <li>
                            <i className="fas fa-mobile-alt"></i>
                            Have your staff download the Haazri mobile app
                        </li>
                        <li>
                            <i className="fas fa-chart-line"></i>
                            Start tracking attendance and monitoring reports
                        </li>
                    </ul>
                </div>
            </div>
        </div>
    );
};

export default PaymentSuccess;
